import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["radio", "submitButton", "selectedId", "chatRoomList", "loading", "fallback"]
  static values = { appScheme: String, csAgentHost: String, defaultProfileImage: String }

  connect() {
    try {
      this.fallbackWebUrl = null
      this.fallbackTimer = null
      this.hideFallbackButton()
      this.updateButton()
      this.setupInfiniteScroll()
      this.checkAndLoadMore()
      this.setupRadioSelectionState()
    } catch (e) {
      console.error("Error in connect:", e)
    }
  }

  setupRadioSelectionState() {
    try {
      this.radioTargets.forEach(input => this.bindRadioSelectionState(input))
      this.refreshRadioSelectionState()
    } catch (e) {
      console.error("Error in setupRadioSelectionState:", e)
    }
  }

  bindRadioSelectionState(input) {
    if (input.dataset.selectionStateBound === 'true') {
      this.updateSelectedState(input)
      return
    }

    this.updateSelectedState(input)
    input.addEventListener('change', () => {
      this.refreshRadioSelectionState()
    })
    input.dataset.selectionStateBound = 'true'
  }

  refreshRadioSelectionState() {
    this.radioTargets.forEach(radio => this.updateSelectedState(radio))
  }
  
  updateSelectedState(input) {
    try {
      const label = input.closest('.seed-select-box')
      if (input.checked) {
        label.setAttribute('data-selected', '')
      } else {
        label.removeAttribute('data-selected')
      }
    } catch (e) {
      console.error("Error in updateSelectedState:", e)
    }
  }

  updateButton() {
    try {
      const selectedRadio = this.radioTargets.find(radio => radio.checked)

      if (selectedRadio) {
        this.submitButtonTarget.value = "분쟁 조정을 신청해요"
        this.submitButtonTarget.disabled = false
        this.selectedIdTarget.value = selectedRadio.value
      } else {
        this.submitButtonTarget.value = "채팅방을 선택해주세요"
        this.submitButtonTarget.disabled = true
        this.selectedIdTarget.value = ""
      }

      this.clearFallbackTimer()
      this.fallbackWebUrl = null
      this.hideFallbackButton()
    } catch (e) {
      console.error("Error in updateButton:", e)
    }
  }

  async navigateToAgent(event) {
    event.preventDefault()
    const form = event.currentTarget?.form || event.target?.form

    if (!this.appSchemeValue || !this.csAgentHostValue) {
      this.submitSelectionForm(form)
      return
    }

    const selectedRadio = this.radioTargets.find(radio => radio.checked)
    if (!selectedRadio) return

    const channelId = selectedRadio.value

    try {
      const checkDuplicateUrl = new URL("/wv/conflict_resolutions/check_duplicate", window.location.origin)
      checkDuplicateUrl.searchParams.set("chat_room_id", channelId)

      const response = await fetch(checkDuplicateUrl.toString(), {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (response.status === 401) {
        alert("로그인이 필요합니다.")
        return
      }

      if (response.status === 403) {
        alert("접근할 수 없는 채팅방입니다.")
        return
      }

      if (!response.ok) {
        throw new Error(`check_duplicate failed with status ${response.status}`)
      }

      const data = await response.json()

      if (data.duplicate) {
        alert("이미 해당 채팅방에 대한 분쟁조정 신청이 존재합니다.")
        return
      }
    } catch (e) {
      console.error("Error checking duplicate:", e)
    }

    const csAgentUrl = new URL("/conflict-resolution/", this.csAgentHostValue)
    csAgentUrl.searchParams.set("channel_id", channelId)
    csAgentUrl.searchParams.set("referrer", "select_chat_room")

    const deepLink = `${this.appSchemeValue}minikarrot/router?remote=${encodeURIComponent(csAgentUrl.toString())}&navbar=false&scrollable=true`
    this.clearFallbackTimer()
    this.fallbackWebUrl = csAgentUrl.toString()
    this.hideFallbackButton()
    this.fallbackTimer = window.setTimeout(() => {
      this.fallbackTimer = null
      if (document.visibilityState !== "visible") return
      this.showFallbackButton()
    }, 1500)
    window.location.href = deepLink
  }

  submitSelectionForm(form) {
    if (!form) return

    if (typeof form.requestSubmit === "function") {
      form.requestSubmit()
    } else {
      form.submit()
    }
  }

  continueOnWeb(event) {
    event.preventDefault()
    this.clearFallbackTimer()
    if (this.fallbackWebUrl) {
      window.location.href = this.fallbackWebUrl
      return
    }

    // 안전장치: fallback URL이 없으면 기존 웹 플로우를 유지합니다.
    const form = this.submitButtonTarget?.form
    this.submitSelectionForm(form)
  }

  clearFallbackTimer() {
    if (!this.fallbackTimer) return

    window.clearTimeout(this.fallbackTimer)
    this.fallbackTimer = null
  }

  showFallbackButton() {
    if (!this.hasFallbackTarget) return

    this.fallbackTarget.hidden = false
  }

  hideFallbackButton() {
    if (!this.hasFallbackTarget) return

    this.fallbackTarget.hidden = true
  }

  setupInfiniteScroll() {
    this.currentPage = 0
    this.isLoading = false
    this.hasMore = true
    this.pageSize = parseInt(this.element.dataset.pageSize) || 10

    this.scrollHandler = this.handleScroll.bind(this)
    window.addEventListener('scroll', this.scrollHandler)
  }

  disconnect() {
    this.clearFallbackTimer()
    window.removeEventListener('scroll', this.scrollHandler)
  }

  handleScroll() {
    if (this.isLoading || !this.hasMore) return
    
    const scrollHeight = Math.max(
      document.body.scrollHeight, document.documentElement.scrollHeight,
      document.body.offsetHeight, document.documentElement.offsetHeight,
      document.body.clientHeight, document.documentElement.clientHeight
    )
    
    if (window.innerHeight + window.pageYOffset >= scrollHeight - 200) {
      this.loadMoreChannels()
    }
  }

  loadMoreChannels() {
    this.isLoading = true
    this.currentPage++
    this.showLoading()
    
    const url = `/wv/conflict_resolutions/load_more_channels?page=${this.currentPage}&page_size=${this.pageSize}`
    
    fetch(url, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        console.error('Error loading channels:', data.error)
        alert('채팅방 정보를 불러오는 중 오류가 발생했습니다.')
        return
      }
      
      this.hasMore = data.has_more
      this.appendChannels(data.channels)
      this.checkAndLoadMore()
    })
    .catch(error => {
      console.error('Error loading channels:', error)
      alert('채팅방 정보를 불러오는 중 오류가 발생했습니다.')
    })
    .finally(() => {
      this.isLoading = false
      this.hideLoading()
    })
  }

  showLoading() {
    this.loadingTarget.style.display = 'block'
  }
  
  hideLoading() {
    this.loadingTarget.style.display = 'none'
  }

  appendChannels(channels) {
    try {
      if (!channels || channels.length === 0) {
        return
      }
      
      if (!this.chatRoomListTarget || !this.loadingTarget) {
        return
      }
      
      const fragment = document.createDocumentFragment()
      
      channels.forEach((channel, index) => {
        try {
          if (!channel.id) {
            return
          }
          
          const label = document.createElement('label')
          label.className = 'seed-select-box'
          label.setAttribute('data-part', 'radio')
          label.setAttribute('data-channel-id', channel.id)
          
          const profileImageUrl = channel.receiver?.profile_image_url || this.defaultProfileImageValue
          const nickname = channel.receiver?.nickname || '알 수 없음'
          const articleTitle = channel.article_title || '제목 없음'
          
          label.innerHTML = `
            <div data-part="content">
              <div class="chat-rooms__profile-container">
                <img src="${profileImageUrl}" class="chat-rooms__profile-image" style="width: 48px; height: 48px;">
                
                <div class="chat-rooms__info">
                  <span data-part="label" class="chat-rooms__receiver-name">${nickname}</span>
                  <span data-part="description" class="chat-rooms__article-title">${articleTitle}</span>
                </div>
              </div>
            </div>
            
            <input 
              type="radio" 
              name="chat_room_id" 
              value="${channel.id}"
              data-action="change->chat-room-selection#updateButton"
              data-chat-room-selection-target="radio"
              class="seed-select-box-input"
            >
            
            <div data-part="radio-control">
              <div data-part="radio-icon" class="radio-icon"></div>
            </div>
          `
          
          fragment.appendChild(label)
        } catch (channelError) {
          console.error(`Error processing channel ${index}:`, channelError)
        }
      })
      
      this.chatRoomListTarget.insertBefore(fragment, this.loadingTarget)
      this.setupRadioSelectionState()
    } catch (e) {
      console.error("Error in appendChannels:", e)
    }
  }

  checkAndLoadMore() {
    const scrollHeight = Math.max(
      document.body.scrollHeight, document.documentElement.scrollHeight,
      document.body.offsetHeight, document.documentElement.offsetHeight,
      document.body.clientHeight, document.documentElement.clientHeight
    )
    
    if (window.innerHeight >= scrollHeight && this.hasMore) {
      this.loadMoreChannels()
    }
  }
}
